import { useMessageSource } from 'react-message-source';
import { TableResizeOptions } from '@uiParts/resizeCheckboxTable/useTableResize';
import { OptionItem } from '@uiParts/selectBox';

/** テーブル一覧表示: 1ページあたりの標準表示件数 */
export const LIST_SIZE_PER_PAGE = 20;

/** テーブル一覧表示: 1ページあたりの標準拡張表示件数 */
export const LIST_SIZE_PER_PAGE_LARGE = 100;

/** 建物の契約単価の限度 */
export const CONTRACT_PRICE_UNIT_LIMIT = 9999999999;

/** int32最大数 */
export const MAX_INT_32 = 2147483647;

/** 数値入力における有効桁数の上限値 */
export const MAX_SIGNIFICANT_FIGURES = 15;

/** 文字連結用のデフォルト文字 */
export const DEFAULT_JOIN_CHARACTER = ',';

/** 契約状況ID */
export const CONTRACT_STATUS_IN = 1; // 契約中
export const CONTRACT_STATUS_END = 2; // 契約終了

/** 計画で使用される対応状況ID */
export const STATUS_UNFIXED = 1; // 未確定
export const STATUS_FIXED = 2; // 確定
export const STATUS_IN_PROGRESS = 3; // 対応中
export const STATUS_CHECK = 4; // 査収待ち
export const STATUS_COMPLETE = 5; // 査収完了
export const STATUS_PENDING = 6; // 案件保留

/**案件で使用される対応状況ID */
export const WORK_STATUS_NOT_STARTED = 1; // 未実施
export const WORK_STATUS_IN_PROGRESS = 2; // 対応中
export const WORK_STATUS_CHECK = 3; // 査収待ち
export const WORK_STATUS_COMPLETE = 4; // 査収完了
export const WORK_STATUS_PENDING = 5; // 保留

/** 削除操作を示すInputEvent.inputTypeのリスト */
export const DELETE_INPUT_TYPE_LIST = [
  'deleteContentBackward',
  'deleteContentForward',
  'deleteWordBackward',
  'deleteWordForward',
];

/** 都道府県マスタ */
export const MST_PREFECTURES = [
  { id: 1, name: '北海道' },
  { id: 2, name: '青森県' },
  { id: 3, name: '岩手県' },
  { id: 4, name: '宮城県' },
  { id: 5, name: '秋田県' },
  { id: 6, name: '山形県' },
  { id: 7, name: '福島県' },
  { id: 8, name: '茨城県' },
  { id: 9, name: '栃木県' },
  { id: 10, name: '群馬県' },
  { id: 11, name: '埼玉県' },
  { id: 12, name: '千葉県' },
  { id: 13, name: '東京都' },
  { id: 14, name: '神奈川県' },
  { id: 15, name: '新潟県' },
  { id: 16, name: '富山県' },
  { id: 17, name: '石川県' },
  { id: 18, name: '福井県' },
  { id: 19, name: '山梨県' },
  { id: 20, name: '長野県' },
  { id: 21, name: '岐阜県' },
  { id: 22, name: '静岡県' },
  { id: 23, name: '愛知県' },
  { id: 24, name: '三重県' },
  { id: 25, name: '滋賀県' },
  { id: 26, name: '京都府' },
  { id: 27, name: '大阪府' },
  { id: 28, name: '兵庫県' },
  { id: 29, name: '奈良県' },
  { id: 30, name: '和歌山県' },
  { id: 31, name: '鳥取県' },
  { id: 32, name: '島根県' },
  { id: 33, name: '岡山県' },
  { id: 34, name: '広島県' },
  { id: 35, name: '山口県' },
  { id: 36, name: '徳島県' },
  { id: 37, name: '香川県' },
  { id: 38, name: '愛媛県' },
  { id: 39, name: '高知県' },
  { id: 40, name: '福岡県' },
  { id: 41, name: '佐賀県' },
  { id: 42, name: '長崎県' },
  { id: 43, name: '熊本県' },
  { id: 44, name: '大分県' },
  { id: 45, name: '宮崎県' },
  { id: 46, name: '鹿児島県' },
  { id: 47, name: '沖縄県' },
];

/** @summary 定期業務区分IDに紐づく業務種別IDを定義する */
export const BUSINESS_TYPE_LIST_LINKED_INSPECTION_DIVISION: { [key: string]: number[] } = {
  /** 定期業務区分: 設備 */
  '1': [
    1, // 空気調和設備
    2, // 給排水設備
    3, // 昇降機設備
    4, // 弱電設備
    5, // 強電設備
    6, // 消防用設備
    12, // 環境衛生業務
  ],
  /** 定期業務区分: 清掃 */
  '2': [
    7, // 清掃業務
  ],
  /** 定期業務区分: 警備 */
  '3': [
    8, // 警備業務
  ],
  /** 定期業務区分: 検針 */
  '4': [
    9, // 検針
  ],
  /** 定期業務区分: 建築 */
  '5': [
    10, // 建築付帯設備
  ],
  /** 定期業務区分: その他 */
  '6': [
    11, // マネジメント業務
    99, // その他
  ],
};

/**
 * @summary システムで利用する共通の定数を定義する
 * @description 定数に定義したid値はAPI引き渡し用データとして利用する
 * フックもしくは、画面側においては以下のように利用する
 *【フック側コード】例
 * const [fixedTermTaskType, setFixedTermTaskType] = useState('');
 * const fixedTermTaskTypes: OptionItem[] = MST_FIXED_TERM_TASK_TYPES.map((v) => ({
 *    title: v.name,
 *    value: v.id,
 * }));
 *
 * const handleFixedTermTaskTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
 *   setFixedTermTaskType(e.target.value);
 * };
 * return {
 *   // Type 定期業務種別 Value、 Option、Changeイベントハンドラー を画面へ提供
 *   fixedTermTaskType,
 *   fixedTermTaskTypes,
 *   handleFixedTermTaskTypeChange,
 * };
 *【画面側コード】例
 * const {
 *   fixedTermTaskType,
 *   fixedTermTaskTypes,
 *   handleFixedTermTaskTypeChange,
 * } = useFilterOptions();
 *
 * <SelectBox
 *   value={fixedTermTaskType}
 *   options={fixedTermTaskTypes}
 *   onChange={handleFixedTermTaskTypeChange}
 *   headerValue={''}
 *   headerTitle={''}
 * />
 * @returns  定義したシステム共通で利用できる定数
 */
export const useConstants = () => {
  const { getMessage } = useMessageSource();

  /**
   * @summary 定期業務種別(Type)のAPI引き渡し用Idの管理テーブル
   */
  const MST_FIXED_TERM_TASK_TYPES = [
    { id: 1, name: getMessage(`common.fixedTermTaskType.0001`) },
    { id: 2, name: getMessage(`common.fixedTermTaskType.0002`) },
    { id: 3, name: getMessage(`common.fixedTermTaskType.0003`) },
    { id: 4, name: getMessage(`common.fixedTermTaskType.0004`) },
  ];

  /**
   * @summary 定期業務区分(Division)のAPI引き渡し用Idの管理テーブル
   */
  const MST_FIXED_TERM_TASK_DIVISIONS = [
    { id: 1, name: getMessage(`common.fixedTermTaskDivision.0001`) },
    { id: 2, name: getMessage(`common.fixedTermTaskDivision.0002`) },
    { id: 3, name: getMessage(`common.fixedTermTaskDivision.0003`) },
    { id: 4, name: getMessage(`common.fixedTermTaskDivision.0004`) },
    { id: 5, name: getMessage(`common.fixedTermTaskDivision.0005`) },
    { id: 6, name: getMessage(`common.fixedTermTaskDivision.0006`) },
  ];

  /**
   * @summary 対応状況(Status)のAPI引き渡し用Idの管理テーブル
   */
  const MST_FIXED_TERM_TASK_STATUSES = [
    { id: 1, name: getMessage(`common.fixedTermTaskStatus.0001`) },
    { id: 2, name: getMessage(`common.fixedTermTaskStatus.0002`) },
    { id: 3, name: getMessage(`common.fixedTermTaskStatus.0003`) },
    { id: 4, name: getMessage(`common.fixedTermTaskStatus.0004`) },
    { id: 5, name: getMessage(`common.fixedTermTaskStatus.0005`) },
    { id: 6, name: getMessage(`common.fixedTermTaskStatus.0006`) },
  ];

  /**
   * @summary 案件分類
   * @description
   * 1.不具合・2.問い合わせ・3.その他・4.工事
   */
  const WORK_TYPE_OPTIONS: OptionItem[] = [
    { title: getMessage(`common.workTypeOptions.0001`), value: 1 },
    { title: getMessage(`common.workTypeOptions.0002`), value: 2 },
    { title: getMessage(`common.workTypeOptions.0004`), value: 3 },
    { title: getMessage(`common.workTypeOptions.0003`), value: 4 },
  ];

  /**
   * @summary 優先度
   * @description
   * 1.高・2中・3.低
   */
  const WORK_PRIORITY_OPTIONS: OptionItem[] = [
    { title: getMessage(`common.workPriorityOption.0001`), value: 1 },
    { title: getMessage(`common.workPriorityOption.0002`), value: 2 },
    { title: getMessage(`common.workPriorityOption.0003`), value: 3 },
  ];

  /**
   * @summary 対応状況
   * @description
   * 1.未実施・2対応中・3.査収待ち・4.査収完了・5.保留
   */
  const WORK_STATUS_OPTIONS: OptionItem[] = [
    { title: getMessage(`common.workStatusOptions0001`), value: 1 },
    { title: getMessage(`common.workStatusOptions0002`), value: 2 },
    { title: getMessage(`common.workStatusOptions0003`), value: 3 },
    { title: getMessage(`common.workStatusOptions0004`), value: 4 },
    { title: getMessage(`common.workStatusOptions0005`), value: 5 },
  ];

  /**
   * @summary 対応状況（査収待ち前段階）
   * @description
   * 1.未実施・2対応中・5.保留
   */

  const WORK_CHANGEABLE_STATUS_OPTIONS: OptionItem[] = [
    { title: getMessage(`common.workStatusOptions0001`), value: 1 },
    { title: getMessage(`common.workStatusOptions0002`), value: 2 },
    { title: getMessage(`common.workStatusOptions0005`), value: 5 },
  ];

  const QUALIFICATION_STATUS_OPTIONS: OptionItem[] = [
    { title: getMessage('common.QualificationStatus.0001'), value: 0 },
    { title: getMessage('common.QualificationStatus.0002'), value: 1 },
    { title: getMessage('common.QualificationStatus.0003'), value: 2 },
    { title: getMessage('common.QualificationStatus.0004'), value: 3 },
    { title: getMessage('common.QualificationStatus.0005'), value: 4 },
  ];
  const QUALIFICATION_REPRESENTATIVE_OPTIONS: OptionItem[] = [
    { title: getMessage('common.QualificationRepresentative.0001'), value: 0 },
    { title: getMessage('common.QualificationRepresentative.0002'), value: 1 },
    { title: getMessage('common.QualificationRepresentative.0003'), value: 2 },
  ];
  const QUALIFICATION_ASSIGNTYPE_OPTIONS: OptionItem[] = [
    { title: getMessage('common.QualificationAssignmentType.0001'), value: 0 },
    { title: getMessage('common.QualificationAssignmentType.0002'), value: 1 },
  ];

  return {
    MST_FIXED_TERM_TASK_TYPES,
    MST_FIXED_TERM_TASK_DIVISIONS,
    MST_FIXED_TERM_TASK_STATUSES,
    WORK_TYPE_OPTIONS,
    WORK_PRIORITY_OPTIONS,
    WORK_STATUS_OPTIONS,
    WORK_CHANGEABLE_STATUS_OPTIONS,
    QUALIFICATION_STATUS_OPTIONS,
    QUALIFICATION_REPRESENTATIVE_OPTIONS,
    QUALIFICATION_ASSIGNTYPE_OPTIONS,
  };
};

/** リサイズテーブルを使用するテーブル */
export type USE_RESIZE_TABLE_PAGE =
  /** ホーム 本日の定期業務 */
  | 'homeTask'
  /** ホーム 査収待ちの定期業務 */
  | 'homeCheckingTask'
  /** ホーム 未完了の工事 */
  | 'homeIncompleteConstruction'
  /** ホーム 査収待ちの工事 */
  | 'homeCheckingConstruction'
  /** ホーム 未完了の案件 */
  | 'homeIncompleteWork'
  /** ホーム 査収待ちの案件 */
  | 'homeCheckingWork'
  /** 建物一覧 */
  | 'property'
  /** 建物一覧(建物全体公開ON) */
  | 'propertyPublic'
  /** 契約一覧 */
  | 'contract'
  /** 設備一覧 */
  | 'equipment'
  /** 年次計画 - 予定表 */
  | 'annualSchedule'
  /** 年次計画 - 計画集計 */
  | 'annualTable'
  /** 月次計画 - 予定表 */
  | 'monthlySchedule'
  /** 月次計画 - 計画管理 */
  | 'monthlyTable'
  /** 日次計画 - 予定表 */
  | 'dailySchedule'
  /** 査収一覧 */
  | 'checking'
  /** 定期業務一覧 */
  | 'inspection'
  /** 工事一覧 */
  | 'construction'
  /** 案件一覧 */
  | 'work'
  /** 月報 */
  | 'reportPropertyList'
  | 'reportCompanyList'
  | 'reportTargetList'
  | 'reportSelectFormatList'
  | 'reportFormatList'
  /** ドキュメント */
  | 'archive'
  /** 点検表一覧 */
  | 'inspectionChecklist'
  /** 連絡先一覧 */
  | 'affiliateUser'
  /** 企業一覧 */
  | 'affiliateCompany'
  /** ユーザー管理 */
  | 'user'
  /** グループ管理 */
  | 'group'
  /** 法定資格管理 */
  | 'qualifications'
  /** 法定資格管理履歴 */
  | 'qualificationHistory'
  /** 法定資格管理 - 個別資格 */
  | 'specialQualifications'
  /** 法定資格管理 - 個別資格振り分け */
  | 'specialQualificationsSelection'
  /** ユーザー/企業単一選択モーダル */
  | 'selectAffiliateModal'
  /** 設備選択モーダル */
  | 'selectEquipmentCommonModal';

/**
 * @summary リサイズテーブルの設定
 * @description
 * hasCheckbox: true とした場合は、返却配列columnWidthsの先頭にカラムが追加される。
 * fixedColumnWidths は、チェックボックス分をのぞいた幅を指定する
 * fixedColumnMinWidths は、チェックボックス分をのぞいた幅を指定する
 */
export const RESIZE_TABLE_OPTIONS: { [key in USE_RESIZE_TABLE_PAGE]: TableResizeOptions } = {
  /** ホーム 本日の定期業務 */
  homeTask: {
    hasCheckbox: false,
    fixedColumnWidths: [
      180, // 点検状況
      130, // 計画開始日時
      130, // 最終更新日時
      100, // 点検分類
      150, // 建物名
      500, // 計画名
      110, // 作業者
      110, // 実施者
      110, // 監督者
    ],
    fixedColumnMinWidths: [
      90, // 点検状況
      120, // 計画開始日時
      120, // 最終更新日時
      90, // 点検分類
      85, // 建物名
      85, // 計画名
      85, // 作業者
      85, // 実施者
      85, // 監督者
    ],
  },
  /** ホーム 査収待ちの定期業務 */
  homeCheckingTask: {
    hasCheckbox: false,
    fixedColumnWidths: [
      180, // 点検状況
      130, // 計画開始日時
      130, // 最終更新日時
      100, // 点検分類
      150, // 建物名
      250, // 計画名
      250, // コメント
      110, // 作業者
      110, // 実施者
      110, // 監督者
    ],
    fixedColumnMinWidths: [
      90, // 点検状況
      120, // 計画開始日時
      120, // 最終更新日時
      90, // 点検分類
      85, // 建物名
      85, // 計画名
      85, // コメント
      85, // 作業者
      85, // 実施者
      85, // 監督者
    ],
  },
  /** ホーム 未完了の工事 */
  homeIncompleteConstruction: {
    hasCheckbox: false,
    fixedColumnWidths: [
      180, // 対応状況
      130, // 開始日
      130, // 最終更新日時
      100, // 種類
      150, // 建物名
      500, // 計画名
      110, // 作業者
      110, // 最新実施者
      110, // 監督者
    ],
    fixedColumnMinWidths: [
      90, // 対応状況
      85, // 開始日
      120, // 最終更新日時
      60, // 種類
      85, // 建物名
      85, // 計画名
      85, // 作業者
      100, // 最新実施者
      85, // 監督者
    ],
  } /** ホーム 査収待ちの工事 */,
  homeCheckingConstruction: {
    hasCheckbox: false,
    fixedColumnWidths: [
      180, // 対応状況
      130, // 開始日
      130, // 最終更新日時
      100, // 種類
      150, // 建物名
      250, // 計画名
      250, // コメント
      110, // 作業者
      110, // 最新実施者
      110, // 監督者
    ],
    fixedColumnMinWidths: [
      90, // 対応状況
      85, // 開始日
      120, // 最終更新日時
      60, // 種類
      85, // 建物名
      85, // 計画名
      85, // コメント
      85, // 作業者
      100, // 最新実施者
      85, // 監督者
    ],
  },
  /** ホーム 未完了の案件 */
  homeIncompleteWork: {
    hasCheckbox: false,
    fixedColumnWidths: [
      100, // 対応状況
      80, // 優先度
      130, // 開始日
      130, // 最終更新日時
      100, // 作業分類
      150, // 建物名
      250, // 計画名
      360, // コメント
      220, // 最新実施者
    ],
    fixedColumnMinWidths: [
      90, // 対応状況
      70, // 優先度
      85, // 開始日
      120, // 最終更新日時
      90, // 作業分類
      85, // 建物名
      85, // 計画名
      85, // コメント
      100, // 最新実施者
    ],
  } /** ホーム 査収待ちの案件 */,
  homeCheckingWork: {
    hasCheckbox: false,
    fixedColumnWidths: [
      100, // 対応状況
      80, // 優先度
      130, // 開始日
      130, // 最終更新日時
      100, // 作業分類
      150, // 建物名
      250, // 計画名
      360, // コメント
      220, // 最新実施者
    ],
    fixedColumnMinWidths: [
      90, // 対応状況
      70, // 優先度
      85, // 開始日
      120, // 最終更新日時
      90, // 作業分類
      85, // 建物名
      85, // 計画名
      85, // コメント
      100, // 最新実施者
    ],
  },
  /** 建物一覧 */
  property: {
    hasCheckbox: true,
    fixedColumnWidths: [
      300, // 建物名
      80, // 契約数
      80, // 異常のある設備数
      80, // 未完了の工事数
      80, // 未完了の案件数
      60, // URL
      300, // 住所
      200, // 所有者
      120, // 電話番号
      200, // 階数
      200, // 駐車場
      120, // 築年数
      200, // 建築面積
      120, // 契約先
      100, // 更新日
      200, // 更新者
      // 200, // 非公開状況
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      78, // 契約数
      78, // 異常のある設備数
      78, // 未完了の工事数
      78, // 未完了の案件数
      55, // URL
      70, // 住所
      70, // 所有者
      80, // 電話番号
      60, // 階数
      70, // 駐車場
      70, // 築年数
      80, // 建築面積
      70, // 契約先
      70, // 更新日
      70, // 更新者
      // 100, // 非公開状況
    ],
  },
  /** 建物一覧 */
  propertyPublic: {
    hasCheckbox: true,
    fixedColumnWidths: [
      300, // 建物名
      80, // 契約数
      80, // 異常のある設備数
      80, // 未完了の工事数
      80, // 未完了の案件数
      60, // URL
      300, // 住所
      200, // 所有者
      120, // 電話番号
      200, // 階数
      200, // 駐車場
      120, // 築年数
      200, // 建築面積
      120, // 契約先
      100, // 更新日
      200, // 更新者
      200, // 非公開状況
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      78, // 契約数
      78, // 異常のある設備数
      78, // 未完了の工事数
      78, // 未完了の案件数
      55, // URL
      70, // 住所
      70, // 所有者
      80, // 電話番号
      60, // 階数
      70, // 駐車場
      70, // 築年数
      80, // 建築面積
      70, // 契約先
      70, // 更新日
      70, // 更新者
      100, // 非公開状況
    ],
  },
  /** 契約一覧 */
  contract: {
    hasCheckbox: true,
    fixedColumnWidths: [
      300, // 建物名
      200, // 計画名
      100, // 契約種別
      120, // 契約先
      120, // テナント
      150, // 定期業務種別
      150, // 定期業務区分
      150, // 業務種別
      150, // 業務科目
      150, // 業務対象の名前
      120, // 監督者
      120, // 作業者
      90, // 周期
      120, // 開始日
      120, // 終了日
      160, // 協力会社名
      120, // 請求単価
      120, // 支払単価
      100, // 契約状況
      80, // 点検表
      160, // 報告書フォーマット
    ],
    fixedColumnMinWidths: [
      70, // 建物名
      80, // 計画名
      95, // 契約種別
      70, // 契約先
      80, // テナント
      130, // 定期業務種別
      110, // 定期業務区分
      120, // 業務種別
      120, // 業務科目
      120, // 業務対象の名前
      70, // 監督者
      70, // 作業者
      60, // 周期
      80, // 開始日
      70, // 終了日
      100, // 協力会社名
      85, // 請求単価
      85, // 支払単価
      85, // 契約状況
      70, // 点検表
      150, // 報告書フォーマット
    ],
  },
  /** 設備一覧 */
  equipment: {
    hasCheckbox: true,
    fixedColumnWidths: [
      160, // 設備名
      160, // 機器種別
      160, // 機器科目
      100, // 型番
      200, // 建物名
      100, // フロア
      140, // 部屋
      120, // 設置年月
      120, // 経過年数
      100, // 点検中
      110, // 案件対応中
      100, // 異常
    ],
    fixedColumnMinWidths: [
      80, // 設備名
      95, // 機器種別
      85, // 機器科目
      70, // 型番
      80, // 建物名
      80, // フロア
      70, // 部屋
      85, // 設置年月
      85, // 経過年数
      70, // 点検中
      100, // 案件対応中
      60, // 異常
    ],
  },
  /** 年次計画 - 予定表 */
  annualSchedule: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 建物名
      100, // 契約種別
      130, // 定期業務区分
      130, // 業務種別
      130, // 計画名
      130, // 定期業務種別
      100, // 周期
      160, // カスタム設定
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      95, // 契約種別
      120, // 定期業務区分
      95, // 業務種別
      80, // 計画名
      120, // 定期業務種別
      60, // 周期
      115, // カスタム設定
    ],
  },
  /** 年次計画 - 計画集計 */
  annualTable: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 建物名
      140, // 計画完了月
      120, // 契約種別
      130, // 定期業務区分
      130, // 定期業務種別
      150, // 計画名
      140, // 請求額
      140, // 支払額
      140, // 粗利額
      140, // 粗利率
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      110, // 計画完了月
      95, // 契約種別
      120, // 定期業務区分
      120, // 定期業務種別
      80, // 計画名
      140, // 請求額
      140, // 支払額
      140, // 粗利額
      140, // 粗利率
    ],
  },
  /** 月次計画 - 予定表 */
  monthlySchedule: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 建物名
      100, // 契約種別
      130, // 定期業務区分
      130, // 業務種別
      130, // 計画名
      130, // 定期業務種別
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      95, // 契約種別
      120, // 定期業務区分
      95, // 業務種別
      80, // 計画名
      120, // 定期業務種別
    ],
  },
  /** 月次計画 - 計画管理 */
  monthlyTable: {
    hasCheckbox: true,
    fixedColumnWidths: [
      200, // 建物名
      140, // 計画名
      120, // 契約種別
      130, // 定期業務区分
      130, // 定期業務種別
      100, // 対応状況
      120, // 開始日
      100, // 開始時間
      120, // 終了日
      110, // 終了時間
      140, // 監督者
      140, // 作業者
      140, // 契約先担当者
      140, // テナント担当者
      140, // 協力会社名
      140, // 予定調整先
      140, // 作業メモ
      100, // 自動メール送信状況 - 予定調整
      100, // 自動メール送信状況 - 確定連絡
      100, // 自動メール送信状況 - 事前連絡
      100, // 自動メール送信状況 - 報告依頼
      120, // 契約詳細で設定された予定日時 - 開始日
      100, // 契約詳細で設定された予定日時 - 開始時間
      120, // 契約詳細で設定された予定日時 - 終了日
      100, // 契約詳細で設定された予定日時 - 終了時間
      140, // 販売結果 - 請求額
      140, // 販売結果 - 支払額
      140, // 販売結果 - 粗利額
      100, // 販売結果 - 粗利率
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      80, // 計画名
      95, // 契約種別
      120, // 定期業務区分
      120, // 定期業務種別
      85, // 対応状況
      70, // 開始日
      85, // 開始時間
      70, // 終了日
      85, // 終了時間
      70, // 監督者
      70, // 作業者
      120, // 契約先担当者
      140, // テナント担当者
      110, // 協力会社名
      110, // 予定調整先
      85, // 作業メモ
      85, // 自動メール送信状況 - 予定調整
      85, // 自動メール送信状況 - 確定連絡
      85, // 自動メール送信状況 - 事前連絡
      85, // 自動メール送信状況 - 報告依頼
      70, // 契約詳細で設定された予定日時 - 開始日
      85, // 契約詳細で設定された予定日時 - 開始時間
      70, // 契約詳細で設定された予定日時 - 終了日
      85, // 契約詳細で設定された予定日時 - 終了時間
      70, // 販売結果 - 請求額
      70, // 販売結果 - 支払額
      70, // 販売結果 - 粗利額
      70, // 販売結果 - 粗利率
    ],
  },
  /** 日次計画 - 予定表 */
  dailySchedule: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 建物名
      100, // 契約種別
      130, // 定期業務区分
      130, // 業務種別
      130, // 計画名
      130, // 定期業務種別
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      95, // 契約種別
      120, // 定期業務区分
      95, // 業務種別
      80, // 計画名
      120, // 定期業務種別
    ],
  },
  /** 査収一覧 */
  checking: {
    hasCheckbox: true,
    fixedColumnWidths: [
      50, // ▽
      130, // 計画種別
      100, // 開始日
      120, // 最終更新日
      100, // 作業分類
      200, // 計画名
      90, // 異常件数
      200, // コメント
      120, // 最新実施者
      200, // 建物名
    ],
    fixedColumnMinWidths: [
      40, // ▽
      95, // 計画種別
      80, // 開始日
      110, // 最終更新日
      95, // 作業分類
      70, // 計画名
      85, // 異常件数
      85, // コメント
      110, // 最新実施者
      80, // 建物名
    ],
  },
  /** 定期業務一覧 */
  inspection: {
    hasCheckbox: false,
    fixedColumnWidths: [
      110, // 対応状況
      140, // 計画開始日時
      140, // 最終更新日
      120, // 点検分類
      250, // 計画名
      250, // コメント
      120, // 監督者
      120, // 作業者
      120, // 実施者
      120, // 承認者
      140, // 最終更新者
      120, // 建物名
    ],
    fixedColumnMinWidths: [
      95, // 対応状況
      120, // 計画開始日時
      110, // 最終更新日
      95, // 点検分類
      80, // 計画名
      85, // コメント
      70, // 監督者
      70, // 作業者
      70, // 実施者
      70, // 承認者
      110, // 最終更新者
      70, // 建物名
    ],
  },
  /** 工事一覧 */
  construction: {
    hasCheckbox: false,
    fixedColumnWidths: [
      150, // 対応状況
      110, // 開始日
      470, // 計画名
      130, // 最新実施者
      400, // コメント
      140, // 最終更新日時
      170, // 建物名
    ],
    fixedColumnMinWidths: [
      95, // 対応状況
      80, // 開始日
      70, // 計画名
      110, // 最新実施者
      85, // コメント
      120, // 最終更新日時
      80, // 建物名
    ],
  },
  /** 案件一覧 */
  work: {
    hasCheckbox: true,
    fixedColumnWidths: [
      100, // 対応状況
      90, // 優先度
      110, // 発生日
      110, // 承認日
      140, // 最終更新日時
      120, // 期限
      130, // 作業分類
      250, // 計画名
      280, // コメント
      130, // 最新実施者
      130, // 建物名
    ],
    fixedColumnMinWidths: [
      95, // 対応状況
      80, // 優先度
      80, // 発生日
      80, // 承認日
      120, // 最終更新日時
      60, // 期限
      95, // 作業分類
      80, // 計画名
      85, // コメント
      100, // 最新実施者
      70, // 建物名
    ],
  },
  /** 月報建物一覧 */
  reportPropertyList: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 名前
      200, // URL
      150, // 住所
      150, // 所有者
      150, // 電話番号
    ],
    fixedColumnMinWidths: [
      200, // 名前
      200, // URL
      100, // 住所
      100, // 所有者
      100, // 電話番号
    ],
  },
  /** 月報契約先一覧 */
  reportCompanyList: {
    hasCheckbox: false,
    fixedColumnWidths: [
      200, // 契約先（企業）
      200, // URL
      150, // 住所
      150, // 電話番号
      150, // 代表者氏名
    ],
    fixedColumnMinWidths: [
      200, // 契約先（企業）
      200, // URL
      100, // 住所
      100, // 電話番号
      100, // 代表者氏名
    ],
  },
  reportTargetList: {
    hasCheckbox: true,
    fixedColumnWidths: [
      180, // 建物名
      180, // 計画名
      180, // 契約先
      100, // 業務種類
      100, // 対応状況
      90, // 承認日
      90, // 開始日
      90, // 終了日
      145, // 繰り返しパターン
      165, // 繰り返しカスタム指定
    ],
    fixedColumnMinWidths: [
      180, // 建物名
      180, // 計画名
      180, // 契約先
      100, // 業務種類
      100, // 対応状況
      90, // 承認日
      90, // 開始日
      90, // 終了日
      145, // 繰り返しパターン
      165, // 繰り返しカスタム指定
    ],
  },
  reportSelectFormatList: {
    hasCheckbox: false,
    fixedColumnWidths: [
      180, // フォーマット名
      180, // 登録日時
      180, // 登録者
    ],
    fixedColumnMinWidths: [
      180, // フォーマット名
      180, // 登録日時
      180, // 登録者
    ],
  },
  reportFormatList: {
    hasCheckbox: false,
    fixedColumnWidths: [
      500, // フォーマット名
      200, // 登録日時
      200, // 登録者
      48, // 削除アイコン
    ],
    fixedColumnMinWidths: [
      150, // フォーマット名
      120, // 登録日時
      120, // 登録者
      48, // 削除アイコン
    ],
  },
  /** ドキュメント */
  archive: {
    hasCheckbox: false,
    fixedColumnWidths: [
      570, // 名前
      140, // 更新日時
      125, // 更新者
      150, // ファイルサイズ
      48, // アイコンエリア
    ],
    fixedColumnMinWidths: [
      70, // 名前
      95, // 更新日時
      80, // 更新者
      135, // ファイルサイズ
      48, // アイコンエリア
    ],
  },
  /** 点検表一覧 */
  inspectionChecklist: {
    hasCheckbox: true,
    fixedColumnWidths: [
      200, // 建物名
      200, // 点検表名
      300, // 点検表の説明
      170, // 参照中の定期業務数
      170, // 報告書フォーマット
      120, // ステータス
    ],
    fixedColumnMinWidths: [
      80, // 建物名
      95, // 点検表名
      115, // 点検表の説明
      165, // 参照中の定期業務数
      165, // 報告書フォーマット
      110, // ステータス
    ],
  },
  /** 連絡先一覧 */
  affiliateUser: {
    hasCheckbox: true,
    fixedColumnWidths: [
      200, // 会社名
      100, // 氏名
      100, // 部署名
      100, // 役職
      160, // TEL1
      160, // TEL2
      160, // 携帯電話
      160, // FAX
      200, // メールアドレス
      200, // URL
      200, // 住所
      200, // 登録日
      100, // 登録者
      200, // 更新日
      100, // 更新者
    ],
    fixedColumnMinWidths: [
      80, // 会社名
      70, // 氏名
      70, // 部署名
      60, // 役職
      65, // TEL1
      65, // TEL2
      85, // 携帯電話
      55, // FAX
      130, // メールアドレス
      55, // URL
      60, // 住所
      80, // 登録日
      80, // 登録者
      70, // 更新日
      70, // 更新者
    ],
  },
  /** 企業一覧 */
  affiliateCompany: {
    hasCheckbox: true,
    fixedColumnWidths: [
      200, // 会社名
      100, // 連絡先数
      100, // 過去の作業件数
      100, // 契約件数
      160, // URL
      160, // 住所
      160, // 電話番号
      110, // 代表者氏名
      100, // 業種
      100, // 更新者
      100, // 更新日
    ],
    fixedColumnMinWidths: [
      80, // 会社名
      95, // 連絡先数
      85, // 過去の作業件数
      85, // 契約件数
      55, // URL
      60, // 住所
      85, // 電話番号
      100, // 代表者氏名
      60, // 業種
      70, // 更新者
      70, // 更新日
    ],
  },
  /** ユーザー管理 */
  user: {
    hasCheckbox: false,
    fixedColumnWidths: [
      160, // ユーザー名
      190, // メールアドレス
      120, // 拠点
      120, // 所属
      120, // 役職
      90, // 管理者
      100, // 状態
    ],
    fixedColumnMinWidths: [
      110, // ユーザー名
      130, // メールアドレス
      70, // 拠点
      70, // 所属
      70, // 役職
      80, // 管理者
      70, // 状態
    ],
  },
  /** グループ管理 */
  group: {
    hasCheckbox: false,
    fixedColumnWidths: [
      250, // グループ名
      530, // 説明
      120, // メンバー数
    ],
    fixedColumnMinWidths: [
      110, // グループ名
      60, // 説明
      110, // メンバー数
    ],
  },
  /** 法定資格管理 */
  qualifications: {
    hasCheckbox: false,
    fixedColumnWidths: [
      80, // 担当形態
      150, // 届出資格
      100, // 届出ステータス
      80, // 種別
      120, // 届出者
      180, // 届出名義
      100, // 選任日
      100, // 解任日
      200, // 所属企業
      120, // 添付ファイル
      120, // 備考
      80, // 編集・履歴
    ],
    fixedColumnMinWidths: [
      60, // 担当形態
      60, // 届出資格
      60, // 届出ステータス
      60, // 種別
      60, // 届出者
      60, // 届出名義
      60, // 選任日
      60, // 解任日
      60, // 所属企業
      60, // 添付ファイル
      60, // 備考
      60, // 編集・履歴
    ],
  },
  qualificationHistory: {
    hasCheckbox: false,
    fixedColumnWidths: [
      50, // NO
      80, // 担当形態
      120, // 届出ステータス
      80, // 種別
      120, // 届出者
      180, // 届出名義
      100, // 選任日
      100, // 解任日
      200, // 所属企業
      120, // 添付ファイル
      120, // 備考
      80, // 削除ボタン
    ],
    fixedColumnMinWidths: [
      50, // NO
      60, // 担当形態
      60, // 届出ステータス
      60, // 種別
      60, // 届出者
      60, // 届出名義
      60, // 選任日
      60, // 解任日
      60, // 所属企業
      60, // 添付ファイル
      60, // 備考
      60, // 削除ボタン
    ],
  },
  /** 法定資格管理 - 個別資格管理 */
  specialQualifications: {
    hasCheckbox: false,
    fixedColumnWidths: [
      300, // 個別資格名
      530, // 説明
      100, // ボタン領域
    ],
    fixedColumnMinWidths: [
      150, // 個別資格名
      150, // 説明
      100, // ボタン領域
    ],
  },
  /** 法定資格管理 - 個別資格振り分け */
  specialQualificationsSelection: {
    hasCheckbox: true,
    fixedColumnWidths: [
      240, // 個別資格名
      500, // 説明
    ],
    fixedColumnMinWidths: [
      110, // 個別資格名
      100, // 説明
    ],
  },
  /** ユーザー/企業単一選択モーダル */
  selectAffiliateModal: {
    hasCheckbox: false,
    fixedColumnWidths: [
      300, // 氏名/会社名
      300, // 所属/住所
    ],
    fixedColumnMinWidths: [
      100, // 氏名/会社名
      100, // 所属/住所
    ],
  },
  /** 設備選択モーダル */
  selectEquipmentCommonModal: {
    hasCheckbox: false,
    fixedColumnWidths: [
      300, // 設備名
      200, // 機器種別
      200, // 機器科目
      200, // 型番
      200, // 製造番号
      150, // メーカー
      100, // フロア
      100, // 部屋
    ],
    fixedColumnMinWidths: [
      50, // 設備名
      50, // 機器種別
      50, // 機器科目
      50, // 型番
      50, // 製造番号
      50, // メーカー
      50, // フロア
      50, // 部屋
    ],
  },
};
